/**
 * One entity's backtest on the real chart: the series the rule reads, drawn by
 * Explorer's own renderer (`dashboard/render-tile.ts`, as the SFOC viewer
 * does), with the rule's stored lines across it and every episode the watcher
 * found shaded where it happened (`docs/plans/argus.md`, *Backtest*: "the real
 * chart ... with the periods the rule would have fired shaded on it").
 *
 * A rule may also carry charts its author chose (T3, migration `0057`): an
 * Explore recipe each, without ship or time. They are tabs above the chart, in
 * the author's order, and Argus's guess is the last tab. A chosen chart is
 * drawn as a dashboard tile would draw it, for this entity and this window,
 * with the Argus marks its plot type can carry (`argus-chart-marks.ts`).
 *
 * Nothing here evaluates (rule 3). The series come from Explorer's plot
 * endpoint, as they would in Explore; the lines are the numbers stored in the
 * rule; the bands are the watcher's episodes, placed at their times. Where the
 * chart and the watcher might disagree - Explore resamples a long range, the
 * watcher reads a 3-minute grid - the bands are the watcher's word.
 */

import { html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type uPlot from 'uplot';
import { LightElement } from '../../lit-base.ts';
import { fetchShipPlots, mergeOverlay, type ShipPlotResult } from '../plot/multi-ship-plot.ts';
import { renderTile, type TileChart } from '../dashboard/render-tile.ts';
import { tileShaping } from '../dashboard/tile-spec.ts';
import { drawRegisteredOverlays } from '../plot/render/overlays.ts';
import { seededPlotState, timeColumn, type PlotState } from '../../state/plot-state.ts';
import { parsePlotState } from '../../state/url-sync.ts';
import { buildShaping } from '../../state/request.ts';
import { DEFAULT_OUTLIERS } from '../../state/store.ts';
import type { RequestShaping } from '../plot/plot-request.ts';
import type { OverlayShape } from '../../state/chart-overlays.ts';
import type { AnyPlotResponse, ArgusBacktestChart, ArgusBacktestResult, ArgusRuleChart } from '../../api/types.ts';
import {
  MARKS_NOTE,
  drawEpisodePoints,
  insideEpisodes,
  lineShapes,
  marksOn,
  scatterShapes,
  type EpisodeSpan,
} from './argus-chart-marks.ts';

type Episode = ArgusBacktestResult['episodes'][number];

/** Watcher times are naive UTC; the chart's x is seconds since the epoch. */
export function utcSeconds(iso: string): number {
  const zoned = /[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`;
  return Date.parse(zoned) / 1000;
}

/** Episodes on the chart's clock. */
export function episodeSpans(episodes: readonly Episode[], stepSeconds = 180): EpisodeSpan[] {
  return episodes.map((e) => ({
    from: utcSeconds(e.start),
    // An episode's last step is a step long: without it a one-reading episode
    // is a band of no width, and nothing is drawn.
    to: utcSeconds(e.end ?? e.last) + stepSeconds,
  }));
}

/** The shapes for one entity: a band per episode, a line per stored number. */
export function backtestShapes(
  spec: ArgusBacktestChart,
  episodes: readonly Episode[],
  stepSeconds = 180,
): OverlayShape[] {
  return lineShapes(spec.lines, episodeSpans(episodes, stepSeconds), spec.left, spec.right);
}

/** A chosen chart's Explore state, from its recipe, on the viewer's preferences. */
export function chosenState(query: string): PlotState {
  return parsePlotState(new URLSearchParams(query), seededPlotState());
}

type ShipPlot = ShipPlotResult | undefined;

/**
 * Series already read, by entity, range and spec - so going back to a ship on
 * the triage page draws at once. Short-lived: a few minutes, a few dozen.
 */
const READ = new Map<string, { at: number; result: Promise<ShipPlot> }>();
const READ_MS = 5 * 60_000;
const READ_MAX = 32;

function readOnce(key: string, fetch: () => Promise<ShipPlot>): Promise<ShipPlot> {
  const hit = READ.get(key);
  if (hit && Date.now() - hit.at < READ_MS) return hit.result;
  const result = fetch();
  READ.set(key, { at: Date.now(), result });
  // A failed read is not kept: the next look asks again.
  result.then(
    (r) => {
      if (!r?.data) READ.delete(key);
    },
    () => READ.delete(key),
  );
  while (READ.size > READ_MAX) READ.delete(READ.keys().next().value!);
  return result;
}

/** The tab each rule was last looked at on, so the next alert of it opens there. */
const TAB_OF_RULE = new Map<string, number>();

/** What one draw needs: what to plot, where from, and what Argus adds. */
interface Plan {
  state: PlotState;
  shaping: RequestShaping;
  source: string | undefined;
  entities: string[];
  /** For the read cache: everything that changes the data. */
  key: string;
  decorate: (data: AnyPlotResponse) => ((u: uPlot) => void) | null;
}

@customElement('argus-backtest-chart')
export class ArgusBacktestChartEl extends LightElement {
  @property({ attribute: false }) spec: ArgusBacktestChart | null = null;
  @property({ attribute: false }) episodes: readonly Episode[] = [];
  @property() entity = '';
  /**
   * Other entities drawn with it, one line each (the triage page's selected
   * ships). The shading stays `entity`'s: its episodes, not theirs. Only a
   * line chart draws them; a chosen chart of another type draws `entity` only.
   */
  @property({ attribute: false }) also: readonly string[] = [];
  /** The range drawn, as naive UTC ISO - the run's, or one episode's surroundings. */
  @property() from = '';
  @property() to = '';
  /** The rule's source, which is also Explore's name for it. */
  @property() source = '';
  /** The charts the rule's author chose, drawn as tabs before Argus's guess. */
  @property({ attribute: false }) charts: readonly ArgusRuleChart[] = [];
  /** Which rule, so its tab is kept from one alert to the next. */
  @property() ruleId = '';

  @state() private status: 'idle' | 'loading' | 'drawn' | 'failed' = 'idle';
  @state() private message = '';
  @state() private note = '';
  @state() private tab = -1;
  private chart: TileChart | null = null;
  private drawKey = '';
  private gen = 0;
  private resizeObs: ResizeObserver | null = null;

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.resizeObs?.disconnect();
    this.resizeObs = null;
    this.chart?.destroy();
    this.chart = null;
    this.drawKey = '';
  }

  /** The tab on screen: a chosen chart's index, or `charts.length` for the guess. */
  private current(): number {
    const n = this.charts.length;
    const picked = this.tab >= 0 ? this.tab : (TAB_OF_RULE.get(this.ruleId) ?? 0);
    return Math.min(Math.max(picked, 0), n);
  }

  private pick(i: number): void {
    this.tab = i;
    if (this.ruleId) TAB_OF_RULE.set(this.ruleId, i);
  }

  protected override updated(): void {
    const chosen = this.charts[this.current()] ?? null;
    const key = JSON.stringify([this.entity, this.also, this.from, this.to, this.spec, this.episodes.length, this.source, chosen]);
    if (key !== this.drawKey) {
      this.drawKey = key;
      void this.draw(chosen);
    }
  }

  private mount(): HTMLElement | null {
    return this.querySelector<HTMLElement>('.argus-btc-mount');
  }

  private size(): { width: number; height: number } {
    const mount = this.mount();
    const legend = this.chart?.legendEl();
    const legendH = legend ? Math.ceil(legend.getBoundingClientRect().height) : 0;
    return {
      width: Math.max(160, mount?.clientWidth ?? 600),
      height: Math.max(160, (mount?.clientHeight ?? 300) - legendH),
    };
  }

  /** Take the drawn chart away: only once there is something to put in its place. */
  private clear(): void {
    this.chart?.destroy();
    this.chart = null;
    this.mount()?.replaceChildren();
  }

  private fail(message: string): void {
    this.clear();
    this.status = 'failed';
    this.message = message;
  }

  /** Argus's guess: the series the rule reads, its lines and its episodes. */
  private guessPlan(spec: ArgusBacktestChart): Plan {
    // The person's chart preferences - legend, text size, line width - as
    // every other chart they look at; only what to plot is the rule's.
    const state = { ...seededPlotState(), plot_type: 'line' as const, y_left: [...spec.left], y_right: [...spec.right] };
    const shapes = backtestShapes(spec, this.episodes);
    return {
      state,
      shaping: buildShaping({ filterRows: [], mode: 'builder', derived: [], outliers: { ...DEFAULT_OUTLIERS } }),
      source: this.source || undefined,
      entities: [this.entity, ...this.also.filter((e) => e !== this.entity)],
      key: JSON.stringify(spec),
      decorate: () => (u) => drawRegisteredOverlays(u, shapes),
    };
  }

  /** A chart the author chose, for this entity, with what Argus can add to it. */
  private chosenPlan(chart: ArgusRuleChart): Plan {
    const params = new URLSearchParams(chart.query);
    const state = chosenState(chart.query);
    const type = state.plot_type;
    const shaping = buildShaping(tileShaping(params));
    // shortcut: the time column rides along as a slicer column so a scatter
    // knows each point's time; a plain field on PlotRequestOpts if more need it.
    if (type === 'scatter') Object.assign(shaping, { slice_columns: [timeColumn()] });
    const lines = this.spec?.lines ?? [];
    const spans = episodeSpans(this.episodes);
    const entities = type === 'line' ? [this.entity, ...this.also.filter((e) => e !== this.entity)] : [this.entity];
    return {
      state,
      shaping,
      source: params.get('ds') || this.source || undefined,
      entities,
      key: chart.query,
      decorate: (data) => {
        if (type === 'line') {
          const shapes = lineShapes(lines, spans, state.y_left, state.y_right);
          return (u) => drawRegisteredOverlays(u, shapes);
        }
        if (type === 'scatter' && data.plot_type === 'scatter') {
          const shapes = scatterShapes(lines, state.x_column, state.y_left, state.y_right);
          const times = data.slice_values?.[timeColumn()] ?? [];
          const inside = insideEpisodes(times, spans);
          const hasRight = data.series.some((s) => s.axis === 'right');
          const scales = data.series.map((s) => (s.axis === 'right' && hasRight ? 'yR' : 'y'));
          return (u) => {
            drawRegisteredOverlays(u, shapes);
            drawEpisodePoints(u, inside, scales);
          };
        }
        return null;
      },
    };
  }

  private async draw(chosen: ArgusRuleChart | null): Promise<void> {
    const gen = ++this.gen;
    const spec = this.spec;
    if (!this.entity) return this.fail('No ship to draw.');
    if (!chosen && (!spec || !spec.left.length)) return this.fail('This rule reads no series a chart can draw.');
    const plan = chosen ? this.chosenPlan(chosen) : this.guessPlan(spec!);
    // The chart drawn stays, dimmed, while the next one is read: switching
    // ships must not blank the page.
    this.status = 'loading';
    this.note = chosen && !marksOn(plan.state.plot_type) ? MARKS_NOTE : '';
    const from = this.from;
    const to = this.to;
    // One read per entity, each kept on its own, so selecting one more ship
    // reads only that ship.
    const results = await Promise.all(
      plan.entities.map((entity) =>
        readOnce(JSON.stringify([entity, from, to, plan.source, plan.key]), async () => {
          const [one] = await fetchShipPlots([entity], {
            state: plan.state,
            shaping: plan.shaping,
            start: from,
            end: to,
            ...(plan.source ? { source: plan.source } : {}),
          });
          return one;
        }),
      ),
    );
    if (gen !== this.gen) return;
    const read = results.filter((r): r is ShipPlotResult => !!r);
    const data = plan.entities.length === 1 ? read[0]?.data : mergeOverlay(read);
    if (!data || data.plot_type !== plan.state.plot_type) {
      return this.fail(read.find((r) => r.error)?.error ?? 'No data for this range.');
    }
    await this.updateComplete;
    const target = this.mount();
    const tooltip = this.querySelector<HTMLElement>('.uplot-tooltip');
    if (!target || !tooltip || gen !== this.gen) return;
    const hook = plan.decorate(data);
    // The new chart draws into its own layer over the old one; the old layer
    // goes only when the new one is on screen.
    const old = this.chart;
    const oldLayers = [...target.children];
    const layer = document.createElement('div');
    layer.className = 'argus-btc-layer';
    target.appendChild(layer);
    const chart = await renderTile({ mount: layer, tooltip, container: this }, data, plan.state, this.size(), {
      ...(hook
        ? {
            decorate: (opts: uPlot.Options) => {
              opts.hooks = opts.hooks ?? {};
              // Last, on top of what the chart draws for itself - the same place
              // Explore draws a registered overlay.
              opts.hooks.draw = [...(opts.hooks.draw ?? []), hook];
            },
          }
        : {}),
    });
    if (gen !== this.gen) {
      chart?.destroy();
      layer.remove();
      return;
    }
    old?.destroy();
    for (const node of oldLayers) node.remove();
    this.status = 'drawn';
    this.chart = chart;
    this.chart?.resize(this.size());
    if (!this.resizeObs) {
      this.resizeObs = new ResizeObserver(() => this.chart?.resize(this.size()));
      this.resizeObs.observe(target);
    }
  }

  private renderTabs(): TemplateResult | typeof nothing {
    if (!this.charts.length) return nothing;
    const on = this.current();
    const tab = (i: number, title: string, guess = false): TemplateResult =>
      html`<button
        type="button"
        role="tab"
        class="argus-btc-tab ${i === on ? 'is-on' : ''} ${guess ? 'is-guess' : ''}"
        aria-selected=${i === on ? 'true' : 'false'}
        title=${guess ? 'The chart Argus picks from the rule: the series it reads' : title}
        @click=${() => this.pick(i)}
      >
        ${title}
      </button>`;
    return html`<div class="argus-btc-tabs" role="tablist" aria-label="Charts">
      ${this.charts.map((c, i) => tab(i, c.title))} ${tab(this.charts.length, "Argus's guess", true)}
    </div>`;
  }

  override render(): TemplateResult {
    return html`
      <div class="argus-btc">
        ${this.renderTabs()}
        <div class="argus-btc-mount ${this.status === 'loading' && this.chart ? 'is-stale' : ''}"></div>
        <div class="uplot-tooltip"></div>
        ${this.status === 'loading' && !this.chart
          ? html`<p class="argus-btc-note argus-lfaint">Reading the series…</p>`
          : this.status === 'failed'
            ? html`<p class="argus-btc-note argus-lfaint">${this.message}</p>`
            : nothing}
        ${this.note && this.status !== 'failed' ? html`<p class="argus-lfaint argus-btc-marks">${this.note}</p>` : nothing}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'argus-backtest-chart': ArgusBacktestChartEl;
  }
}
